import {
  ResolvedStacking, StackableProperty, Point,
  buildStackGroups, parseStackKey, getPriceRangeText, getSpiderOffsets,
  clusterIconHtml, stackedBadgeHtml,
} from './mapStacking'
import { createSpiderfyController } from './spiderfyController'

/* Toolkit map-stacking port, Google provider (#1203; Leaflet shipped first
   in #854). The pure halves — co-location grouping, price ranges, cluster
   tiers, fan geometry, badge HTML — come from mapStacking.ts unchanged, and
   the open/collapse state machine from spiderfyController.ts. This module
   owns only the Google glue: density grouping over the map projection (the
   Leaflet side hands that to Leaflet.markercluster), overlay visibility per
   mode, the cluster click rule, and the spiderfy hooks. Every Google object
   arrives injected (the `google` namespace, the map, an OverlayView factory
   from GoogleMap.tsx), so this file unit-tests against stubs — no real map,
   no `@googlemaps/markerclusterer` dependency. */

export type LatLngLike = { lat: number; lng: number }

export type ClusterPoint = { id: string } & LatLngLike

export type ClusterGroup = {
  /** `cluster:` + sorted member ids — stable across an unchanged partition. */
  key: string
  ids: string[]
  /** Bubble anchor: the members' mean coordinate. */
  lat: number
  lng: number
}

/* The radius the Leaflet port hands markercluster (maxClusterRadius: 60). */
export const CLUSTER_RADIUS_PX = 60

/* Radius grouping in projected pixel space — the model Leaflet.markercluster's
   DistanceGrid and the toolkit's Google clusterer (MarkerClustererPlus) share:
   a pin joins the nearest existing seed within the radius, else seeds a new
   group. Singles are returned too so the caller holds the whole partition. */
export function buildClusterGroups(
  points: ClusterPoint[],
  toPx: (p: LatLngLike) => Point,
  radiusPx = CLUSTER_RADIUS_PX,
): ClusterGroup[] {
  type Seed = { px: Point; members: ClusterPoint[] }
  const seeds: Seed[] = []
  const r2 = radiusPx * radiusPx
  for (const p of points) {
    const px = toPx(p)
    let home: Seed | undefined
    let best = Infinity
    for (const s of seeds) {
      const dx = s.px.x - px.x
      const dy = s.px.y - px.y
      const d2 = dx * dx + dy * dy
      if (d2 <= r2 && d2 < best) { best = d2; home = s }
    }
    if (home) home.members.push(p)
    else seeds.push({ px, members: [p] })
  }
  return seeds.map(({ members }) => {
    const ids = members.map((m) => m.id)
    const n = members.length
    return {
      key: `cluster:${[...ids].sort().join(',')}`,
      ids,
      lat: members.reduce((sum, m) => sum + m.lat, 0) / n,
      lng: members.reduce((sum, m) => sum + m.lng, 0) / n,
    }
  })
}

export function clusterPartitionKey(groups: ClusterGroup[]): string {
  return groups.map((g) => g.key).sort().join('|')
}

// ── Event adapter ────────────────────────────────────────────────────────

type Handler = (...args: unknown[]) => void

export type GoogleListenerHandle = { remove(): void }

export type GoogleEventNs = {
  addListener(target: unknown, name: string, fn: Handler): GoogleListenerHandle
  addListenerOnce(target: unknown, name: string, fn: Handler): GoogleListenerHandle
}

/* Google settles pans and zooms alike as 'idle'; the controller's
   moveend/zoomend handlers both hang off it. */
const EVENT_NAMES: Record<string, string> = { moveend: 'idle', zoomend: 'idle' }

/* Leaflet-shaped on/off/once over google.maps.event, so the provider-free
   spiderfy controller drives a Google map unchanged. Google removes by
   listener handle, not by function — the adapter keeps that mapping. */
export function googleMapEvents(map: unknown, event: GoogleEventNs) {
  const handles = new Map<Handler, GoogleListenerHandle>()
  const drop = (fn: Handler) => { handles.get(fn)?.remove(); handles.delete(fn) }
  return {
    on(name: string, fn: Handler) {
      drop(fn)
      handles.set(fn, event.addListener(map, EVENT_NAMES[name] ?? name, fn))
    },
    once(name: string, fn: Handler) {
      drop(fn)
      handles.set(fn, event.addListenerOnce(map, EVENT_NAMES[name] ?? name, (...args) => {
        handles.delete(fn)
        fn(...args)
      }))
    },
    off(_name: string, fn: Handler) {
      drop(fn)
    },
  }
}

// ── Fan chrome ───────────────────────────────────────────────────────────

/* A connector leg as a rotated CSS line from the anchor to a leaf offset. The
   Leaflet side draws polylines in a map pane; Google overlays carry the fan in
   pixel space, so plain rotated divs need no zoom repositioning at all. */
export function fanLegStyle(offset: Point): string {
  const length = Math.hypot(offset.x, offset.y)
  const angle = (Math.atan2(offset.y, offset.x) * 180) / Math.PI
  return `width:${length.toFixed(1)}px;transform:rotate(${angle.toFixed(1)}deg)`
}

export function fanChromeHtml(offsets: Point[]): string {
  const legs = offsets.map((o) => `<div class="hfse-spider-leg-line" style="${fanLegStyle(o)}"></div>`).join('')
  return `<div class="hfse-spider-chrome">${legs}<div class="hfse-spider-center"></div></div>`
}

// ── Layer ────────────────────────────────────────────────────────────────

/** What the layer needs from an OverlayView badge (GoogleMap.tsx's BadgeOverlay). */
export type StackOverlay = {
  setMap(map: unknown): void
  isOnMap(): boolean
  focus(): void
  blur(): void
  setOpacity(opacity: number | null): void
}

export type OverlaySpec = {
  lat: number
  lng: number
  html: string
  /** Class on the badge wrapper (routes the stacking CSS). */
  className?: string
  /** Class focus()/blur() toggle on the wrapper; default = the plain-pin class. */
  focusClass?: string
  /** Center the wrapper on the anchor (cluster bubbles carry no translate of their own). */
  centered?: boolean
  /** Spider leaf: container-pixel offset from the anchor, slid out from 0 on add. */
  offset?: Point
  zIndex?: number
  onClick?: () => void
}

export type GoogleMapLike = {
  getZoom(): number | undefined
  getBounds(): { contains(latLng: unknown): boolean } | undefined | null
  getProjection(): { fromLatLngToPoint(latLng: unknown): Point | null } | undefined | null
  fitBounds(bounds: unknown): void
}

export type GoogleNs = {
  maps: {
    LatLng: new (lat: number, lng: number) => unknown
    LatLngBounds: new () => { extend(latLng: unknown): unknown }
    event: GoogleEventNs
  }
}

export type GoogleStackingDeps<P extends StackableProperty> = {
  g: GoogleNs
  map: GoogleMapLike
  stacking: ResolvedStacking
  /** The map's max zoom — where a cluster click spiderfies instead of zooming. */
  maxZoom: number
  showPrice: boolean
  /** The per-property pin overlays, built by the caller and NOT yet on the map. */
  entries: { property: P; overlay: StackOverlay }[]
  createOverlay: (spec: OverlaySpec) => StackOverlay
  badgeHtml: (property: P) => string
  /** A fanned leaf was clicked: open the property popup anchored to it. */
  onLeafClick: (property: P, leaf: StackOverlay) => void
  /** The fan was torn down (collapse, rebuild): close a popup anchored to a leaf. */
  onFanTeardown: () => void
  clusterRadiusPx?: number
}

export type GoogleStackingLayer = {
  /** Sync overlay visibility to the current mode (and zoom, for clusters). */
  refresh(): void
  dispose(): void
  /** Card-hover routing; false → the caller highlights the plain pin itself. */
  setHighlight(propertyId: string, on: boolean): boolean
  openKey(): string | null
}

export function createGoogleStackingLayer<P extends StackableProperty>(deps: GoogleStackingDeps<P>): GoogleStackingLayer {
  const { g, map, stacking, maxZoom, showPrice, entries, createOverlay, badgeHtml, onLeafClick, onFanTeardown } = deps
  const radius = deps.clusterRadiusPx ?? CLUSTER_RADIUS_PX
  const byId = new Map(entries.map((e) => [e.property.id, e]))
  const points: ClusterPoint[] = []
  for (const { property } of entries) {
    const lat = property.location?.lat
    const lng = property.location?.lng
    if (lat && lng) points.push({ id: property.id, lat, lng })
  }

  // Live groups by key: stacks are fixed for the layer's life, clusters
  // follow the zoom. A member id maps to the group currently hiding its pin.
  const groups = new Map<string, { ids: string[] } & LatLngLike>()
  const groupOverlays = new Map<string, StackOverlay>()
  const memberKey = new Map<string, string>()
  let partition: string | null = null
  let fan: { key: string; leaves: { id: string; overlay: StackOverlay }[]; chrome: StackOverlay } | null = null
  let idle: GoogleListenerHandle | null = null

  const latLng = (p: LatLngLike) => new g.maps.LatLng(p.lat, p.lng)
  const show = (o: StackOverlay) => { if (!o.isOnMap()) o.setMap(map) }
  const hide = (o: StackOverlay) => { if (o.isOnMap()) o.setMap(null) }

  const spider = createSpiderfyController(googleMapEvents(map, g.maps.event), stacking.fadeOpacity, {
    renderFan: (key) => {
      const group = groups.get(key)
      if (!group?.ids.length) return null
      const offsets = getSpiderOffsets(group.ids.length)
      const chrome = createOverlay({
        lat: group.lat, lng: group.lng, html: fanChromeHtml(offsets),
        className: 'hfse-spider-center-wrap', zIndex: 600,
        onClick: () => spider.collapse(),
      })
      chrome.setMap(map)
      const leaves: { id: string; overlay: StackOverlay }[] = []
      group.ids.forEach((id, i) => {
        const entry = byId.get(id)
        if (!entry) return
        const leaf = createOverlay({
          lat: group.lat, lng: group.lng, html: badgeHtml(entry.property),
          className: 'hfse-spider-leaf', focusClass: 'hfse-map-badge-parent-focus',
          offset: offsets[i], zIndex: 601,
          onClick: () => onLeafClick(entry.property, leaf),
        })
        leaf.setMap(map)
        leaves.push({ id, overlay: leaf })
      })
      fan = { key, leaves, chrome }
      return () => {
        leaves.forEach(({ overlay }) => overlay.setMap(null))
        chrome.setMap(null)
        fan = null
        onFanTeardown()
      }
    },
    // Leaves ride pixel offsets on their own overlay containers and Google
    // re-draws every overlay per pan/zoom — nothing to reposition.
    repositionFan: () => {},
    isAnchorVisible: (key) => {
      const group = groups.get(key)
      if (!group) return false
      return !!map.getBounds()?.contains(latLng(group))
    },
    setFade: (opacity) => {
      entries.forEach(({ overlay }) => overlay.setOpacity(opacity))
      groupOverlays.forEach((o) => o.setOpacity(opacity))
    },
    onOpen: (key) => { const o = groupOverlays.get(key); if (o) hide(o) },
    onClose: (key) => { const o = groupOverlays.get(key); if (o) show(o) },
  })

  // ── Stacked-badge mode: exactly co-located pins, fixed per build ──
  if (!stacking.clusterMode) {
    const stacks = buildStackGroups(entries.map((e) => e.property))
    for (const [key, ids] of stacks) {
      const { lat, lng } = parseStackKey(key)
      groups.set(key, { ids, lat, lng })
      ids.forEach((id) => memberKey.set(id, key))
      const members = ids.map((id) => byId.get(id)?.property).filter((p): p is P => !!p)
      const priceRange = showPrice ? getPriceRangeText(members) : ''
      groupOverlays.set(key, createOverlay({
        lat, lng, html: stackedBadgeHtml(priceRange, ids.length),
        className: 'hfse-stack-wrapper', focusClass: 'hfse-map-badge-parent-focus', zIndex: 500,
        onClick: () => spider.toggle(key),
      }))
    }
  }

  function syncStacks() {
    entries.forEach(({ property, overlay }) => (memberKey.has(property.id) ? hide(overlay) : show(overlay)))
    groupOverlays.forEach((o, key) => { if (spider.openKey() !== key) show(o) })
  }

  // ── Cluster-primary mode: recomputed per zoom on idle ──
  function refreshClusters() {
    const proj = map.getProjection()
    const zoom = map.getZoom()
    if (!proj || zoom == null) {
      // Not initialized yet (no projection before the first idle): plain
      // pins until then — the Leaflet side renders plain until markercluster
      // has loaded, same shape.
      entries.forEach(({ overlay }) => show(overlay))
      return
    }
    const scale = Math.pow(2, zoom)
    const toPx = (p: LatLngLike): Point => {
      const w = proj.fromLatLngToPoint(latLng(p))
      return w ? { x: w.x * scale, y: w.y * scale } : { x: 0, y: 0 }
    }
    const clusters = buildClusterGroups(points, toPx, radius)
    const next = clusterPartitionKey(clusters)
    if (next === partition) return
    partition = next
    // The partition moved under an open fan: its bubble no longer exists.
    spider.collapse()
    groupOverlays.forEach((o) => hide(o))
    groupOverlays.clear()
    groups.clear()
    memberKey.clear()
    for (const c of clusters) {
      if (c.ids.length < 2) {
        const single = byId.get(c.ids[0])
        if (single) show(single.overlay)
        continue
      }
      c.ids.forEach((id) => {
        memberKey.set(id, c.key)
        const entry = byId.get(id)
        if (entry) hide(entry.overlay)
      })
      groups.set(c.key, c)
      const bubble = createOverlay({
        lat: c.lat, lng: c.lng, html: clusterIconHtml(c.ids.length).html,
        className: 'hfse-cluster-wrap', centered: true,
        onClick: () => clusterClick(c.key),
      })
      bubble.setMap(map)
      groupOverlays.set(c.key, bubble)
    }
  }

  // Toolkit / markercluster rule: a bubble zooms to its members until the
  // map's max zoom, where it spiderfies instead. Members on one identical
  // coordinate never separate — fitBounds on a point lands at max zoom, and
  // the next click fans them out. The zoom is the visitor's own gesture, not
  // programmatic framing (same as markercluster's zoomToBounds on Leaflet).
  function clusterClick(key: string) {
    const c = groups.get(key)
    if (!c) return
    if ((map.getZoom() ?? 0) >= maxZoom) {
      spider.toggle(key)
      return
    }
    const bounds = new g.maps.LatLngBounds()
    c.ids.forEach((id) => {
      const loc = byId.get(id)?.property.location
      if (loc?.lat && loc.lng) bounds.extend(latLng({ lat: loc.lat, lng: loc.lng }))
    })
    map.fitBounds(bounds)
  }

  if (stacking.clusterMode) idle = g.maps.event.addListener(map, 'idle', refreshClusters)

  return {
    refresh: () => (stacking.clusterMode ? refreshClusters() : syncStacks()),
    dispose: () => {
      idle?.remove()
      idle = null
      spider.dispose()
      groupOverlays.forEach((o) => hide(o))
      groupOverlays.clear()
      groups.clear()
      memberKey.clear()
      partition = null
    },
    // Hover routing (#854 rule): a fanned leaf highlights; a stacked member
    // highlights its stack badge; a pin folded into a cluster bubble has
    // nothing of its own to invert (swallowed); a visible pin falls through.
    setHighlight: (propertyId, on) => {
      const method = on ? 'focus' : 'blur'
      fan?.leaves.forEach((leaf) => { if (leaf.id === propertyId) leaf.overlay[method]() })
      const key = memberKey.get(propertyId)
      if (!key) return false
      if (!stacking.clusterMode) groupOverlays.get(key)?.[method]()
      return true
    },
    openKey: () => spider.openKey(),
  }
}
